import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function GoogleButton({ label }) {
  return (
    <button type="button" onClick={googleLogin} className="btn-ghost w-full" data-testid="google-login-btn">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 20-7.6 20-21 0-1.2-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 16 3 9.1 7.6 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9 41.4 16 45 24 45z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.3 5.2C40.8 35.6 44 30.4 44 24c0-1.2-.1-2.3-.4-3.5z"/>
      </svg>
      {label}
    </button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 grid place-items-center px-5 py-12">
      <Seo title="Log in — MeasureTheRoof" description="Log in to order aerial roof measurement reports and view your report history." path="/login" />
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="card-mtr">
          <h1 className="font-body font-extrabold text-navy text-[24px] mb-1">Log in</h1>
          <p className="text-mortar text-[14px] mb-6">Welcome back. Let's measure some roofs.</p>

          <GoogleButton label="Continue with Google" />
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-gravel flex-1" /><span className="text-[12px] text-mortar uppercase tracking-wider">or</span><div className="h-px bg-gravel flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="email" className="label-mtr">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-mtr pl-11" placeholder="you@company.com" data-testid="login-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label-mtr">Password</label>
                <Link to="/forgot-password" className="text-[12px] text-orange font-semibold" data-testid="forgot-password-link">Forgot?</Link>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-mtr pl-11" placeholder="••••••••" data-testid="login-password" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Log in"}
            </button>
          </form>
        </div>
        <p className="text-center text-[14px] text-mortar mt-5">
          New here?{" "}
          <Link to="/signup" className="text-orange font-bold" data-testid="go-signup">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
